import { Box, useMediaQuery } from "@mui/material";
import { STATICS } from "api/api";
import { Icon } from "components/Icons";
import { ImgWrapper } from "components/ImgContainer/ImgWrapper";
import React, { useEffect, useState } from "react";
import { get } from "state/jotai";
import { _project } from "state/store.projects";
import styled from "styled-components";
import { Controller, FreeMode, Keyboard, Lazy, Pagination, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

export const Gallery = ({ GallerySlider }) => {
  const { ListOfPhotos, Tag } = GallerySlider || {};
  const gallery = get(_project, "gallery");

  const [swiper, setSwiper] = useState(null);
  const [swiperThumbs, setSwiperThumbs] = useState(null);
  const dummyImages = [1, 2, 3, 4, 5];
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTab = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [index, setIndex] = useState(0);

  const pagination = {
    clickable: true,
    renderBullet: function (i, className) {
      return '<span class="' + className + ` ${i === 4 ? " last-dot" : ""}` + '"></span>';
    },
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (swiper) swiper.slidePrev();
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (swiper) swiper.slideNext();
  };

  const photosToShow = Tag
    ? ListOfPhotos?.filter((photo) => photo?.Tags?.includes(Tag))
    : ListOfPhotos;

  const handleSlideChange = () => {
    if (swiper) {
      const bullets = document.querySelectorAll(".swiper-pagination-bullet");
      const activeIndex = swiper.activeIndex;
      const maxVisible = activeIndex !== photosToShow?.length - 1 ? 4 : 5;

      bullets.forEach((bullet, index) => {
        if (activeIndex >= maxVisible - 1) {
          bullet.style.display =
            index < activeIndex - (maxVisible - 1) ? "none" : "inline-block";

          if (index === activeIndex + 1) bullet.classList.add("last-dot");
          else bullet.classList.remove("last-dot");
        } else {
          bullet.style.display = "inline-block";
        }
      });
      setIndex(swiper.activeIndex);
    }
  };

  useEffect(() => {
    if (swiper) {
      swiper.on("slideChange", handleSlideChange);
      return () => swiper.off("slideChange", handleSlideChange);
    }
  }, [swiper]);

  return (
    <Box
      sx={{
        p: isMobile ? "40px 16px" : isTab ? "40px 24px" : "80px 80px 31.5px",
        userSelect: "none",
        "& .swiper-slide": {
          cursor: "pointer",
        },
        "& > .swiper .swiper-slide": {
          width: "100%",
          height: isMobile ? "226px" : "768px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        "& > .swiper .swiper-slide img": {
          height: isMobile ? "226px" : "768px",
          width: "auto",
          maxWidth: "100%",
          objectFit: "contain",
          display: "block",
          margin: "0 auto",
          borderRadius: "16px",
        },
        "& > .swiper .swiper-pagination-bullet.swiper-pagination-bullet-active": {
          background: "#000000",
        },
        "& > .swiper .swiper-pagination-bullet": {
          background: "#83807e",
        },
        "& > .swiper .swiper-pagination-bullet.last-dot": {
          width: "4px",
          height: "4px",
        },
        "& > .swiper .swiper-pagination": {
          borderRadius: "4px",
          width: "88px",
          bottom: isMobile ? "8px" : "16px",
          height: "24px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          padding: "0 8px",
          background: "rgba(255, 255, 255, 0.75)",
          left: "50%",
          transform: "translateX(-50%)",
        },
      }}
    >
      <Swiper
        onSwiper={setSwiper}
        controller={{ control: swiperThumbs }}
        modules={[FreeMode, Keyboard, Thumbs, Controller, Pagination, Lazy]}
        slidesPerView={"auto"}
        pagination={pagination}
        lazy={true}
      >
        {Boolean(ListOfPhotos?.length) && 
        photosToShow?.map((image, i) => (
              <SwiperSlide key={i}>
                 <ImgWrapper alt={`slide-${i}`} imgStyle={{ width: 'auto', borderRadius: '16px', overflow: 'hidden' }} img={image.UrlOriginal} />
              </SwiperSlide>
            ))
        }

        {/* Arrows */}
        <>
          <Arrow
            sx={{ opacity: index ? 1 : 0.5 }}
            left
            isMobile={isMobile}
            onClick={(e) => index && handlePrev(e)}
          >
            <div
              style={{
                borderRadius: "50%",
                backgroundColor: "#ffffffbf",
                display: "flex",
                height: "32px",
                width: "32px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon icon="arrow-left" sx={{ color: "#000", fontSize: 20 }} />
            </div>
          </Arrow>
          <Arrow
            right
            isMobile={isMobile}
            sx={{
              opacity:
                (GallerySlider?.ListOfPhotos?.length
                  ? GallerySlider?.ListOfPhotos?.length - 1
                  : dummyImages.length - 1) !== index
                  ? 1
                  : 0.5,
            }}
            onClick={(e) =>
              (GallerySlider?.ListOfPhotos?.length
                ? GallerySlider?.ListOfPhotos?.length - 1
                : dummyImages.length - 1) !== index && handleNext(e)
            }
          >
            <div
              style={{
                borderRadius: "50%",
                backgroundColor: "#ffffffbf",
                display: "flex",
                height: "32px",
                width: "32px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon icon="arrow-right" sx={{ color: "#000", fontSize: 20 }} />
            </div>
          </Arrow>
        </>
      </Swiper>
    </Box>
  );
};

// STYLES
const Arrow = styled(Box)(({ left, right, isMobile }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  padding: "8px 0px",
  top: "50%",
  transform: "translateY(-50%)",
  left: left ? (isMobile ? "8px" : "24px") : "unset",
  right: right ? (isMobile ? "8px" : "24px") : "unset",
  width: "32px",
  height: "32px",
  backgroundColor: "transparent",
  zIndex: 1000,
  cursor: "pointer",
  "& > span": {
    height: 20,
  },
}));
